import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DateTime, Interval } from 'luxon';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ShopSelector from '@/components/selectors/ShopSelector';
import DateTimeRangeSelector from '@/components/selectors/DateTimeRangeSelector';
import { useShopPersistence } from '@/components/shared/navigation/securePage';
import { useSettingsStore } from '@core/store';

export interface FilterShopDateTimeIntervalData {
    shopId: string;
    start: string;
    end: string;
}

export interface FilterShopDateTimeIntervalProps {
    onLoadData: (data: FilterShopDateTimeIntervalData) => void;
}

export const FilterShopDateTimeInterval = ({ onLoadData }: FilterShopDateTimeIntervalProps) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useSettingsStore();
    const location = useLocation();

    const currentRoundedTime = useCallback(() => {
        const current = DateTime.local().startOf('minute');
        const minute = current.get('minute');
        return current.plus({ minutes: 5 - (minute % 5) });
    }, []);

    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [today] = useState(DateTime.local());
    const [startDate, setStartDate] = useState(DateTime.local().minus({ days: 1 }));
    const [endDate, setEndDate] = useState(DateTime.local());
    const [startTime, setStartTime] = useState(currentRoundedTime());
    const [endTime, setEndTime] = useState(currentRoundedTime());

    const errorMessages = useMemo(() => {
        const interval = Interval.fromDateTimes(startDate.startOf('day'), endDate.endOf('day'));
        return interval.length('days') < 30 ? [] : [t('rangeLt30')];
    }, [startDate, endDate, t]);

    const isSameDay = useMemo(() => {
        return startDate.hasSame(endDate, 'day');
    }, [startDate, endDate]);

    const startTimeMax = useMemo(() => {
        if (!isSameDay) return undefined;
        return endTime.minus({ minutes: 1 });
    }, [isSameDay, endTime]);

    const endTimeMin = useMemo(() => {
        if (!isSameDay) return undefined;
        return startTime.plus({ minutes: 1 });
    }, [isSameDay, startTime]);

    const handleStartTimeChange = useCallback(
        (time: DateTime) => {
            if (isSameDay) {
                const startMinutes = time.hour * 60 + time.minute;
                const endMinutes = endTime.hour * 60 + endTime.minute;
                if (startMinutes >= endMinutes) return;
            }
            setStartTime(time);
        },
        [isSameDay, endTime]
    );

    const handleEndTimeChange = useCallback(
        (time: DateTime) => {
            if (isSameDay) {
                const startMinutes = startTime.hour * 60 + startTime.minute;
                const endMinutes = time.hour * 60 + time.minute;
                if (endMinutes <= startMinutes) return;
            }
            setEndTime(time);
        },
        [isSameDay, startTime]
    );

    const handleStartDateChange = useCallback(
        (date: DateTime) => {
            setStartDate(date);
            if (date.toISODate() === endDate.toISODate()) {
                setStartTime(endTime.minus({ minutes: 5 }));
            }
        },
        [endDate, endTime]
    );

    const handleEndDateChange = useCallback(
        (date: DateTime) => {
            setEndDate(date);
            if (date.toISODate() === startDate.toISODate()) {
                setEndTime(startTime.plus({ minutes: 5 }));
            }
        },
        [startDate, startTime]
    );

    const emitLoadData = useCallback(() => {
        if (!selectedShopIds[0]) return;

        const startUTC = DateTime.utc(
            startDate.year,
            startDate.month,
            startDate.day,
            startTime.hour,
            startTime.minute,
            0,
            0
        );

        const endUTC = DateTime.utc(endDate.year, endDate.month, endDate.day, endTime.hour, endTime.minute).minus({
            milliseconds: 1
        });

        const start = startUTC.toISO();
        const end = endUTC.toISO();

        if (!start || !end) return;

        onLoadData({ shopId: selectedShopIds[0], start, end });
    }, [selectedShopIds, startDate, endDate, startTime, endTime, onLoadData]);

    useEffect(() => {
        if (!selectedShopIds[0] || errorMessages.length > 0) return;
        emitLoadData();
    }, [selectedShopIds[0], startDate, startTime, endDate, endTime]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('shopId')) {
            setTimeout(() => {
                const shopIdParam = params.get('shopId');
                if (shopIdParam) setSelectedShopIds([shopIdParam]);
            }, 500);
        }
        if (params.get('start')) {
            setTimeout(() => {
                const startParam = params.get('start');
                if (startParam) {
                    const parsedDate = DateTime.fromISO(startParam);
                    if (parsedDate.isValid) setStartDate(parsedDate.toLocal());
                }
            }, 500);
        }
        if (params.get('end')) {
            setTimeout(() => {
                const endParam = params.get('end');
                if (endParam) {
                    const parsedDate = DateTime.fromISO(endParam);
                    if (parsedDate.isValid) setEndDate(parsedDate.toLocal());
                }
            }, 500);
        }
    }, [location.search]);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ width: isMobile ? '100%' : 'auto', flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                    <ShopSelector
                        value={selectedShopIds[0] ?? null}
                        onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                        label={t('shop')}
                    />
                </Box>

                <DateTimeRangeSelector
                    startDate={startDate}
                    endDate={endDate}
                    startTime={startTime}
                    endTime={endTime}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onStartTimeChange={handleStartTimeChange}
                    onEndTimeChange={handleEndTimeChange}
                    maxRange={30}
                    locale={i18n.language}
                    error={errorMessages.length > 0}
                    helperText={errorMessages.length > 0 ? errorMessages[0] : undefined}
                    startTimeMax={startTimeMax}
                    endTimeMin={endTimeMin}
                    today={today}
                />
            </Box>
        </LocalizationProvider>
    );
};
